import EmailIcon from '@mui/icons-material/Email';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SmsIcon from '@mui/icons-material/Sms';
import {
  Box,
  Checkbox,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import * as React from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { OrderIcon } from '@/components/OrderIcon';
import { naturalCompare } from '@/util';

import { ErrorBoundary } from '../../../components/ErrorBoundary';
import { getSubscribers } from '../../api';
import {
  getSubscriberById,
  NotificationFlag,
  parseSubscriberUnit,
  parseUnitNotifications,
  updateSubscriberNotifications,
} from '../api';

type SortDirection = 'asc' | 'desc';
type SortOrder = -1 | 0 | 1;
type SortableProperty = 'unit_name' | 'email' | 'first_name' | 'last_name';

type RowData = {
  id: string;
  unit_name: string;
  unit_id: string;
  email: string;
  first_name: string;
  last_name: string;
  leak_alerts_enabled: boolean;
  high_usage_alerts_enabled: boolean;
  sms_leak_alerts_enabled: boolean;
  sms_high_usage_alerts_enabled: boolean;
  loading: boolean;
  saving: boolean;
  error?: string;
};

type Comparator = (a: RowData, b: RowData, orderBy?: SortableProperty) => SortOrder;

interface ISubscribers {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  unit_subscriptions: Array<{
    subscriber_id: string;
    unit_id: string;
    unit_name: string;
    is_billing_contact: boolean;
    move_in_date: null | string;
    move_out_date: null | string;
    has_open_invoices: boolean;
    open_stripe_invoice_ids: any[];
  }>;
}

const descendingComparator = (a: RowData, b: RowData, orderBy: SortableProperty) => {
  const valA = a[orderBy] ?? '';
  const valB = b[orderBy] ?? '';

  if (typeof valA === 'string' && typeof valB === 'string') {
    return naturalCompare(valB, valA);
  }
  if (valB < valA) return -1;
  if (valB > valA) return 1;
  return 0;
};

const getComparator = (order: SortDirection, orderBy: SortableProperty): Comparator => {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy) as SortOrder
    : (a, b) => -descendingComparator(a, b, orderBy) as SortOrder;
};

const stableSort = (array: RowData[], comparator: Comparator): RowData[] => {
  const stabilizedThis = array.map((el, index) => [el, index]);

  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0] as RowData, b[0] as RowData);
    if (order !== 0) return order;
    return (a as number[])[1] - (b as number[])[1];
  });

  return stabilizedThis.map((el) => el[0] as RowData);
};

const subscribersToRows = (subscribersArr: ISubscribers[]): RowData[] => {
  const rows: RowData[] = [];

  subscribersArr.forEach((subscriber) => {
    subscriber.unit_subscriptions.forEach((unit) => {
      rows.push({
        id: subscriber.id,
        unit_name: unit.unit_name,
        unit_id: unit.unit_id,
        email: subscriber.email,
        first_name: subscriber.first_name,
        last_name: subscriber.last_name,
        leak_alerts_enabled: false, // Will be loaded individually
        high_usage_alerts_enabled: false, // Will be loaded individually
        sms_leak_alerts_enabled: false, // Will be loaded individually
        sms_high_usage_alerts_enabled: false, // Will be loaded individually
        loading: true,
        saving: false,
      });
    });
  });

  return rows;
};

// Alert type labels and descriptions
const ALERT_TYPE_LABELS = {
  leak: 'Leak Alerts',
  high_usage: 'High Usage Alerts',
};

const ALERT_TYPE_DESCRIPTIONS = {
  leak: 'Sent when a low flow, continuous leak or abnormal usage is detected.',
  high_usage: 'Sent when a high flow leak with high costs or damage is detected.',
};

// Components
const headCells = [
  { id: 'unit_name', numeric: false, disablePadding: false, label: 'Unit Name' },
  { id: 'email', numeric: false, disablePadding: false, label: 'Email Address' },
  { id: 'first_name', numeric: false, disablePadding: false, label: 'First Name' },
  { id: 'last_name', numeric: false, disablePadding: false, label: 'Last Name' },
];

const alertTypes: Array<keyof typeof ALERT_TYPE_LABELS> = ['leak', 'high_usage'];

type HeadProps = {
  classes: { [x: string]: string };
  order: SortDirection;
  orderBy: SortableProperty;
  onRequestSort: (event: React.MouseEvent, property: SortableProperty) => void;
  leakAlertsSelectAll: boolean;
  highUsageAlertsSelectAll: boolean;
  smsLeakAlertsSelectAll: boolean;
  smsHighUsageAlertsSelectAll: boolean;
  onLeakAlertsSelectAll: () => void;
  onHighUsageAlertsSelectAll: () => void;
  onSmsLeakAlertsSelectAll: () => void;
  onSmsHighUsageAlertsSelectAll: () => void;
  hasData: boolean;
  bulkBusy: boolean;
};

const hiddenArrow = () => <span />;

const EnhancedTableHead = ({
  classes: _classes,
  order,
  orderBy,
  onRequestSort,
  leakAlertsSelectAll,
  highUsageAlertsSelectAll,
  smsLeakAlertsSelectAll,
  smsHighUsageAlertsSelectAll,
  onLeakAlertsSelectAll,
  onHighUsageAlertsSelectAll,
  onSmsLeakAlertsSelectAll,
  onSmsHighUsageAlertsSelectAll,
  hasData,
  bulkBusy,
}: HeadProps) => {
  const createSortHandler = (property: SortableProperty) => (event: React.MouseEvent) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell key={headCell.id} align="center" sortDirection={false}>
            <TableSortLabel
              IconComponent={hiddenArrow}
              onClick={createSortHandler(headCell.id as SortableProperty)}
            >
              {headCell.label}
              {orderBy === headCell.id && (
                <span
                  style={{
                    position: 'absolute',
                    right: '-25px',
                  }}
                >
                  <OrderIcon type={order} />
                </span>
              )}
            </TableSortLabel>
          </TableCell>
        ))}
        {alertTypes.map((alertType) => (
          <TableCell key={alertType} align="center" sx={{ minWidth: '160px' }}>
            <Stack direction="column" alignItems="center" spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>
                  {ALERT_TYPE_LABELS[alertType]}
                </Typography>
                <Tooltip title={ALERT_TYPE_DESCRIPTIONS[alertType]}>
                  <IconButton>
                    <InfoOutlinedIcon
                      sx={{
                        fontSize: '17px',
                        color: '#555',
                        mb: 0.25,
                      }}
                    />
                  </IconButton>
                </Tooltip>
              </Stack>
              <Stack direction="row" spacing={2} alignItems="center">
                <Stack direction="column" alignItems="center" spacing={0.5}>
                  <EmailIcon fontSize="small" />
                  {hasData && (
                    <Checkbox
                      size="small"
                      checked={
                        alertType === 'leak' ? leakAlertsSelectAll : highUsageAlertsSelectAll
                      }
                      onChange={
                        alertType === 'leak' ? onLeakAlertsSelectAll : onHighUsageAlertsSelectAll
                      }
                      disabled={bulkBusy}
                    />
                  )}
                </Stack>
                <Stack direction="column" alignItems="center" spacing={0.5}>
                  <SmsIcon fontSize="small" />
                  {hasData && (
                    <Checkbox
                      size="small"
                      checked={
                        alertType === 'leak' ? smsLeakAlertsSelectAll : smsHighUsageAlertsSelectAll
                      }
                      onChange={
                        alertType === 'leak'
                          ? onSmsLeakAlertsSelectAll
                          : onSmsHighUsageAlertsSelectAll
                      }
                      disabled={bulkBusy}
                    />
                  )}
                </Stack>
              </Stack>
            </Stack>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

const EnhancedTableToolbar = () => {
  return (
    <Box margin={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-end" spacing={0}>
        <Typography variant="h5" id="tableTitle" component="div" fontWeight={600}>
          Tenant Alerts
        </Typography>
      </Stack>
    </Box>
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    padding: `${theme.spacing(4)} 0 ${theme.spacing(3)}`,
  },
  paper: {
    marginBottom: theme.spacing(2),
  },
  table: {
    minWidth: 650,
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  },
}));

export const TenantAlertsTable = (): JSX.Element => {
  const { structureId } = useParams<{ structureId: string }>();
  const [order, setOrder] = React.useState<SortDirection>('asc');
  const [orderBy, setOrderBy] = React.useState<SortableProperty>('email');
  const [page, setPage] = React.useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = React.useState<number>(10);
  const [rows, setRows] = React.useState<RowData[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [leakAlertsSelectAll, setLeakAlertsSelectAll] = React.useState(false);
  const [highUsageAlertsSelectAll, setHighUsageAlertsSelectAll] = React.useState(false);
  const [smsLeakAlertsSelectAll, setSmsLeakAlertsSelectAll] = React.useState(false);
  const [smsHighUsageAlertsSelectAll, setSmsHighUsageAlertsSelectAll] = React.useState(false);
  const mountedRef = React.useRef(true);

  const BULK_TOAST_ID = 'tenant-alerts-bulk';
  const [bulkBusy, setBulkBusy] = React.useState(false);

  const classes = useStyles();

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Load subscribers and their notification settings
  React.useEffect(() => {
    const loadData = async () => {
      if (!structureId) return;

      try {
        setLoading(true);

        // Get all subscribers for this structure
        const subscribersData = await getSubscribers(structureId);
        const subscribers = subscribersData.subscribers || [];

        // Convert to rows
        const initialRows = subscribersToRows(subscribers);
        setRows(initialRows);

        // Load individual notification settings
        for (const row of initialRows) {
          try {
            const root = await getSubscriberById(row.id);
            const us = parseSubscriberUnit(root, row.unit_id);
            const notifications = parseUnitNotifications(us);

            const leakEnabled = notifications.email.includes('LEAK');
            const highUsageEnabled = notifications.email.includes('HIGH_USAGE');
            const smsLeakEnabled = notifications.sms.includes('LEAK');
            const smsHighUsageEnabled = notifications.sms.includes('HIGH_USAGE');

            if (!mountedRef.current) return;

            setRows((prev) =>
              prev.map((prevRow) =>
                prevRow.id === row.id && prevRow.unit_id === row.unit_id
                  ? {
                      ...prevRow,
                      leak_alerts_enabled: leakEnabled,
                      high_usage_alerts_enabled: highUsageEnabled,
                      sms_leak_alerts_enabled: smsLeakEnabled,
                      sms_high_usage_alerts_enabled: smsHighUsageEnabled,
                      loading: false,
                      error: undefined,
                    }
                  : prevRow,
              ),
            );
          } catch (err: any) {
            if (!mountedRef.current) return;

            let errorMessage = 'Failed to load notification settings';
            if (err.message === 'Subscriber not found') {
              errorMessage = 'Subscriber not found';
            } else if (err.message === 'Access denied') {
              errorMessage = 'Access denied';
            }

            setRows((prev) =>
              prev.map((prevRow) =>
                prevRow.id === row.id && prevRow.unit_id === row.unit_id
                  ? {
                      ...prevRow,
                      loading: false,
                      error: errorMessage,
                    }
                  : prevRow,
              ),
            );
          }
        }
      } catch (err: any) {
        const errorMessage = `Failed to load tenant data: ${err.message}`;
        toast.error(errorMessage, { closeButton: true });
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [structureId]);

  // Update select all states based on current data
  React.useEffect(() => {
    const visibleRows = rows.filter((row) => !row.loading && !row.error);
    if (visibleRows.length > 0) {
      const allLeakEnabled = visibleRows.every((row) => row.leak_alerts_enabled);
      const allHighUsageEnabled = visibleRows.every((row) => row.high_usage_alerts_enabled);
      const allSmsLeakEnabled = visibleRows.every((row) => row.sms_leak_alerts_enabled);
      const allSmsHighUsageEnabled = visibleRows.every((row) => row.sms_high_usage_alerts_enabled);
      setLeakAlertsSelectAll(allLeakEnabled);
      setHighUsageAlertsSelectAll(allHighUsageEnabled);
      setSmsLeakAlertsSelectAll(allSmsLeakEnabled);
      setSmsHighUsageAlertsSelectAll(allSmsHighUsageEnabled);
    }
  }, [rows]);

  const handleRequestSort = (event: React.MouseEvent, property: SortableProperty) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) =>
    setPage(newPage);

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleToggleAlert = async (
    rowId: string,
    unitId: string,
    alertType: 'leak' | 'high_usage',
    enabled: boolean,
    notificationType: 'email' | 'sms' = 'email',
    opts?: { silent?: boolean },
  ) => {
    // Set saving state
    setRows((prev) =>
      prev.map((row) =>
        row.id === rowId && row.unit_id === unitId ? { ...row, saving: true } : row,
      ),
    );

    try {
      const row = rows.find((r) => r.id === rowId && r.unit_id === unitId);
      if (!row) throw new Error('Row not found');

      // Get current notifications
      const root = await getSubscriberById(rowId);
      const us = parseSubscriberUnit(root, unitId);
      const notifications = parseUnitNotifications(us);

      let newEmailNotifications = [...notifications.email];
      let newSmsNotifications = [...notifications.sms];

      const flagName = alertType === 'leak' ? 'LEAK' : 'HIGH_USAGE';

      if (notificationType === 'email') {
        if (enabled) {
          if (!newEmailNotifications.includes(flagName)) {
            newEmailNotifications.push(flagName);
          }
        } else {
          newEmailNotifications = newEmailNotifications.filter((flag) => flag !== flagName);
        }
      } else {
        if (enabled) {
          if (!newSmsNotifications.includes(flagName)) {
            newSmsNotifications.push(flagName);
          }
        } else {
          newSmsNotifications = newSmsNotifications.filter((flag) => flag !== flagName);
        }
      }

      const validEmailNotifications = newEmailNotifications.filter(
        (flag): flag is NotificationFlag => ['LEAK', 'HIGH_USAGE'].includes(flag),
      );
      const validSmsNotifications = newSmsNotifications.filter((flag): flag is NotificationFlag =>
        ['LEAK', 'HIGH_USAGE'].includes(flag),
      );

      await updateSubscriberNotifications(
        rowId,
        unitId,
        validEmailNotifications,
        validSmsNotifications,
      );

      // Update local state
      if (!mountedRef.current) return;

      setRows((prev) =>
        prev.map((prevRow) =>
          prevRow.id === rowId && prevRow.unit_id === unitId
            ? {
                ...prevRow,
                leak_alerts_enabled: validEmailNotifications.includes('LEAK'),
                high_usage_alerts_enabled: validEmailNotifications.includes('HIGH_USAGE'),
                sms_leak_alerts_enabled: validSmsNotifications.includes('LEAK'),
                sms_high_usage_alerts_enabled: validSmsNotifications.includes('HIGH_USAGE'),
                saving: false,
              }
            : prevRow,
        ),
      );

      if (!opts?.silent) {
        const id = `row-${rowId}-${unitId}-${alertType}-${notificationType}`;
        const notificationLabel = notificationType === 'email' ? 'Email' : 'SMS';
        const alertLabel = alertType === 'leak' ? 'Leak' : 'High usage';
        const msg = `${notificationLabel} ${alertLabel} alerts ${
          enabled ? 'enabled' : 'disabled'
        } for ${row.first_name} ${row.last_name}`;
        if (toast.isActive(id)) {
          toast.update(id, {
            render: msg,
            type: 'success',
            isLoading: false,
            autoClose: 2000,
            closeButton: true,
          });
        } else {
          toast.success(msg, { toastId: id, autoClose: 2000, closeButton: true });
        }
      }
    } catch (err: any) {
      if (!opts?.silent) {
        const id = `row-${rowId}-${unitId}-${alertType}-${notificationType}`;
        const notificationLabel = notificationType === 'email' ? 'Email' : 'SMS';
        const alertLabel = alertType === 'leak' ? 'leak' : 'high usage';
        const msg = `Failed to update ${notificationLabel} ${alertLabel} alerts: ${err.message}`;
        if (toast.isActive(id)) {
          toast.update(id, {
            render: msg,
            type: 'error',
            isLoading: false,
            autoClose: 4000,
            closeButton: true,
          });
        } else {
          toast.error(msg, { toastId: id, autoClose: 4000, closeButton: true });
        }
      }
    } finally {
      // Reset saving state
      if (mountedRef.current) {
        setRows((prev) =>
          prev.map((row) =>
            row.id === rowId && row.unit_id === unitId ? { ...row, saving: false } : row,
          ),
        );
      }
    }
  };

  const bulkToggle = async (
    rowsToChange: RowData[],
    alertType: 'leak' | 'high_usage',
    enabled: boolean,
    notificationType: 'email' | 'sms' = 'email',
  ) => {
    if (bulkBusy) return;
    setBulkBusy(true);
    try {
      const notificationLabel = notificationType === 'email' ? 'Email' : 'SMS';
      const alertLabel = alertType === 'leak' ? 'Leak' : 'High usage';
      const titleBase = `${notificationLabel} ${alertLabel} alerts ${
        enabled ? 'enabling' : 'disabling'
      }`;

      // Reuse the same toast if it exists; otherwise create it.
      if (toast.isActive(BULK_TOAST_ID)) {
        toast.update(BULK_TOAST_ID, {
          render: `${titleBase}: starting...`,
          isLoading: true,
          type: 'default',
          autoClose: false,
          closeOnClick: false,
          draggable: false,
          closeButton: true,
        });
      } else {
        toast.loading(`${titleBase}: starting...`, {
          toastId: BULK_TOAST_ID,
          closeOnClick: false,
          draggable: false,
          closeButton: true,
        });
      }

      let completed = 0;
      const total = rowsToChange.length;
      const failures: { name: string; reason: string }[] = [];

      const updateProgress = () => {
        toast.update(BULK_TOAST_ID, {
          render: `${titleBase}: ${completed}/${total}`,
          isLoading: true,
          closeButton: true,
        });
      };

      const runOne = async (row: RowData) => {
        try {
          await handleToggleAlert(row.id, row.unit_id, alertType, enabled, notificationType, {
            silent: true,
          });
        } catch (e: any) {
          failures.push({
            name: `${row.first_name} ${row.last_name} (${row.unit_name})`,
            reason: e?.message ?? 'Unknown error',
          });
        } finally {
          completed += 1;
          updateProgress();
        }
      };

      const concurrency = 5;
      const queue = rowsToChange.slice();
      const worker = async () => {
        while (queue.length) {
          const row = queue.shift()!;
          await runOne(row);
        }
      };

      updateProgress();
      await Promise.all(Array.from({ length: Math.min(concurrency, total) }, () => worker()));

      if (failures.length === 0) {
        toast.update(BULK_TOAST_ID, {
          render: `Done: ${titleBase} for ${total}/${total}`,
          type: 'success',
          isLoading: false,
          autoClose: 3000,
          closeButton: true,
        });
      } else {
        const ok = total - failures.length;
        const head = failures
          .slice(0, 3)
          .map((f) => `• ${f.name}: ${f.reason}`)
          .join('\n');
        const tail = failures.length > 3 ? `\n…and ${failures.length - 3} more` : '';
        toast.update(BULK_TOAST_ID, {
          render: `Partial success: ${ok}/${total}\n${head}${tail}`,
          type: 'warning',
          isLoading: false,
          autoClose: 6000,
          closeButton: true,
        });
      }
    } finally {
      setBulkBusy(false);
    }
  };

  const handleLeakAlertsSelectAll = async () => {
    if (bulkBusy) return;
    const newState = !leakAlertsSelectAll;
    const visibleRows = rows.filter((row) => !row.loading && !row.error);
    const rowsToChange = visibleRows.filter((row) => row.leak_alerts_enabled !== newState);

    if (rowsToChange.length === 0) return;

    await bulkToggle(rowsToChange, 'leak', newState, 'email');
  };

  const handleHighUsageAlertsSelectAll = async () => {
    if (bulkBusy) return;
    const newState = !highUsageAlertsSelectAll;
    const visibleRows = rows.filter((row) => !row.loading && !row.error);
    const rowsToChange = visibleRows.filter((row) => row.high_usage_alerts_enabled !== newState);

    if (rowsToChange.length === 0) return;

    await bulkToggle(rowsToChange, 'high_usage', newState, 'email');
  };

  const handleSmsLeakAlertsSelectAll = async () => {
    if (bulkBusy) return;
    const newState = !smsLeakAlertsSelectAll;
    const visibleRows = rows.filter((row) => !row.loading && !row.error);
    const rowsToChange = visibleRows.filter((row) => row.sms_leak_alerts_enabled !== newState);

    if (rowsToChange.length === 0) return;

    await bulkToggle(rowsToChange, 'leak', newState, 'sms');
  };

  const handleSmsHighUsageAlertsSelectAll = async () => {
    if (bulkBusy) return;
    const newState = !smsHighUsageAlertsSelectAll;
    const visibleRows = rows.filter((row) => !row.loading && !row.error);
    const rowsToChange = visibleRows.filter(
      (row) => row.sms_high_usage_alerts_enabled !== newState,
    );

    if (rowsToChange.length === 0) return;

    await bulkToggle(rowsToChange, 'high_usage', newState, 'sms');
  };

  const sortedRows = stableSort(rows, getComparator(order, orderBy));
  const paginatedRows = sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <ErrorBoundary>
      <div className={classes.root}>
        <Paper
          variant="outlined"
          sx={{
            borderColor: 'divider',
            borderWidth: 2,
            borderRadius: 2,
            mt: -2,
          }}
          className={classes.paper}
        >
          <EnhancedTableToolbar />
          <Divider sx={{ borderColor: 'grey', width: '100%', mt: 1 }} />
          <TableContainer>
            <Table className={classes.table} size="medium">
              <EnhancedTableHead
                classes={classes}
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
                leakAlertsSelectAll={leakAlertsSelectAll}
                highUsageAlertsSelectAll={highUsageAlertsSelectAll}
                smsLeakAlertsSelectAll={smsLeakAlertsSelectAll}
                smsHighUsageAlertsSelectAll={smsHighUsageAlertsSelectAll}
                onLeakAlertsSelectAll={handleLeakAlertsSelectAll}
                onHighUsageAlertsSelectAll={handleHighUsageAlertsSelectAll}
                onSmsLeakAlertsSelectAll={handleSmsLeakAlertsSelectAll}
                onSmsHighUsageAlertsSelectAll={handleSmsHighUsageAlertsSelectAll}
                hasData={rows.length > 0}
                bulkBusy={bulkBusy}
              />
              <TableBody sx={{ position: 'relative' }}>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ py: 10 }} align="center">
                      <Box display="flex" flexDirection="column" alignItems="center" gap={3}>
                        <Typography color="text.secondary">No tenants found.</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedRows.map((row) => (
                    <TableRow key={`${row.id}-${row.unit_id}`} hover>
                      <TableCell align="center">{row.unit_name}</TableCell>
                      <TableCell align="center">{row.email}</TableCell>
                      <TableCell align="center">{row.first_name}</TableCell>
                      <TableCell align="center">{row.last_name}</TableCell>
                      {alertTypes.map((alertType) => {
                        const emailChecked =
                          alertType === 'leak'
                            ? row.leak_alerts_enabled
                            : row.high_usage_alerts_enabled;
                        const smsChecked =
                          alertType === 'leak'
                            ? row.sms_leak_alerts_enabled
                            : row.sms_high_usage_alerts_enabled;
                        return (
                          <TableCell key={alertType} align="center">
                            {row.loading ? (
                              <CircularProgress size={18} />
                            ) : (
                              <Stack direction="row" spacing={2} justifyContent="center">
                                <Checkbox
                                  checked={emailChecked}
                                  onChange={(e) =>
                                    handleToggleAlert(
                                      row.id,
                                      row.unit_id,
                                      alertType,
                                      e.target.checked,
                                      'email',
                                    )
                                  }
                                  disabled={row.saving || bulkBusy}
                                  size="small"
                                />
                                <Checkbox
                                  checked={smsChecked}
                                  onChange={(e) =>
                                    handleToggleAlert(
                                      row.id,
                                      row.unit_id,
                                      alertType,
                                      e.target.checked,
                                      'sms',
                                    )
                                  }
                                  disabled={row.saving || bulkBusy}
                                  size="small"
                                />
                              </Stack>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            count={rows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onRowsPerPageChange={handleChangeRowsPerPage}
            component={'div'}
            onPageChange={handleChangePage}
          />
        </Paper>
      </div>
    </ErrorBoundary>
  );
};
